import { useMemo, useState } from 'react'

import { ChartControls, ChartFilters, SectionHeading } from '@/components/ui'
import type { ChartControlsState } from '@/components/ui/chart'
import { useProject } from '@/modules/projects'
import type { MerchantStat, Transaction } from '@/modules/projects/stores/types'
import { MerchantsSection } from './Merchants'
import { FrequencyAmountScatter } from './Merchants/FrequencyAmountScatter'
import { MerchantDetailModal } from './Merchants/MerchantDetailModal'
import { TopMerchantsChart } from './Merchants/TopMerchantsChart'
import { matchMerchantTransactions } from './Merchants/utils'
import { ChartCard } from './shared'

interface MerchantsInsightsProps {
  merchants: MerchantStat[]
  transactions: Transaction[]
}

export function MerchantsInsights({
  merchants,
  transactions,
}: MerchantsInsightsProps) {
  const project = useProject()
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [topChartControls, setTopChartControls] =
    useState<ChartControlsState | null>(null)
  const [scatterChartControls, setScatterChartControls] =
    useState<ChartControlsState | null>(null)

  const selectedMerchant = selectedGroup
    ? (merchants.find((merchant) => merchant.merchantGroup === selectedGroup) ??
      null)
    : null

  const matchedTransactions = useMemo(() => {
    if (!selectedMerchant) return []
    return matchMerchantTransactions(selectedMerchant, transactions)
  }, [selectedMerchant, transactions])

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Merchants"
        description="We bundled spelling variations for the same business into one merchant, so a company that shows up 4 different ways on your statements adds up to one real total instead of getting split across rows. Click any bar, point, or row to see every transaction behind it."
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard
          title="Top merchants"
          description="Biggest counterparties in your ledger by absolute total, money in or out."
          toolbar={
            topChartControls && (
              <ChartFilters>
                <ChartControls
                  {...topChartControls}
                  filename={`${project?.slug}-top-merchants`}
                  projectName={project?.name}
                  title="Top merchants"
                  size="md"
                  floating={false}
                />
              </ChartFilters>
            )
          }
        >
          <TopMerchantsChart
            merchants={merchants}
            onMerchantClick={setSelectedGroup}
            onChartReady={setTopChartControls}
          />
        </ChartCard>

        <ChartCard
          title="Frequency vs amount"
          description="Merchants plotted by transaction count vs. total amount (log scale)."
          toolbar={
            scatterChartControls && (
              <ChartFilters>
                <ChartControls
                  {...scatterChartControls}
                  filename={`${project?.slug}-merchants-frequency-vs-amount`}
                  projectName={project?.name}
                  title="Frequency vs amount"
                  size="md"
                  floating={false}
                />
              </ChartFilters>
            )
          }
        >
          <FrequencyAmountScatter
            merchants={merchants}
            onMerchantClick={setSelectedGroup}
            onChartReady={setScatterChartControls}
          />
        </ChartCard>
      </div>

      <MerchantsSection
        merchants={merchants}
        onMerchantClick={setSelectedGroup}
        exportFilename={`${project?.slug}-merchants`}
      />

      <MerchantDetailModal
        merchant={selectedMerchant}
        matchedTransactions={matchedTransactions}
        onOpenChange={(open) => {
          if (!open) setSelectedGroup(null)
        }}
      />
    </div>
  )
}
